import React, { useState } from "react";
import { amountLabel, fmtDate, fmtDateWithWeekday, rangeLabel, rupeesWhole } from "../format";

const dayLabel = (iso) => fmtDate(iso).replace(/ \d{4}$/, "");

export default function TrendHero({ daily, range, compareRange, amount = "food" }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const series = daily || [];
  const amountName = amountLabel(amount);
  const subtitle = range
    ? `${rangeLabel(range)}${compareRange ? `, compared with ${rangeLabel(compareRange)}` : ""}`
    : "Daily operations performance";

  if (!daily) {
    return (
      <div className="ins-card" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div className="ins-card-header">
          <div>
            <h3 className="ins-card-title">Orders per day &amp; Revenue Trend</h3>
            <p className="ins-card-subtitle">{subtitle}</p>
          </div>
        </div>
        <div className="ins-card-body" style={{ minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
          Loading trend analytics…
        </div>
      </div>
    );
  }

  if (series.length < 2) {
    return (
      <div className="ins-card" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div className="ins-card-header">
          <div>
            <h3 className="ins-card-title">Orders per day &amp; Revenue Trend</h3>
            <p className="ins-card-subtitle">{subtitle}</p>
          </div>
        </div>
        <div className="ins-card-body" style={{ minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
          Pick a range of two days or more to see the interactive trend.
        </div>
      </div>
    );
  }

  const W = 800;
  const H = 240;
  const padLeft = 48;
  const padRight = 44;
  const padTop = 20;
  const padBottom = 30;
  const chartW = W - padLeft - padRight;
  const chartH = H - padTop - padBottom;

  const maxRev = Math.max(1, ...series.map((d) => d.revenue || 0));
  const maxDone = Math.max(1, ...series.map((d) => d.delivered || 0));

  const points = series.map((d, i) => {
    const x = padLeft + (i / (series.length - 1)) * chartW;
    const revY = padTop + chartH - ((d.revenue || 0) / maxRev) * chartH;
    const doneY = padTop + chartH - ((d.delivered || 0) / maxDone) * chartH;
    return { x, revY, doneY, date: d.date, revenue: d.revenue || 0, delivered: d.delivered || 0 };
  });

  const makeCurvePath = (pts, yKey) => {
    if (pts.length === 0) return "";
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0][yKey].toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i];
      const p1 = pts[i + 1];
      const cx1 = p0.x + (p1.x - p0.x) / 2;
      const cy1 = p0[yKey];
      const cx2 = p0.x + (p1.x - p0.x) / 2;
      const cy2 = p1[yKey];
      d += ` C ${cx1.toFixed(1)} ${cy1.toFixed(1)}, ${cx2.toFixed(1)} ${cy2.toFixed(1)}, ${p1.x.toFixed(1)} ${p1[yKey].toFixed(1)}`;
    }
    return d;
  };

  const revLine = makeCurvePath(points, "revY");
  const doneLine = makeCurvePath(points, "doneY");
  const bottomY = padTop + chartH;
  const revArea = `${revLine} L ${points[points.length - 1].x.toFixed(1)} ${bottomY} L ${points[0].x.toFixed(1)} ${bottomY} Z`;
  const doneArea = `${doneLine} L ${points[points.length - 1].x.toFixed(1)} ${bottomY} L ${points[0].x.toFixed(1)} ${bottomY} Z`;

  const gridSteps = [0.25, 0.5, 0.75, 1];

  const activePt = hoverIndex !== null && points[hoverIndex] ? points[hoverIndex] : null;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (clientX - padLeft) / chartW));
    const idx = Math.round(ratio * (series.length - 1));
    if (idx >= 0 && idx < series.length) {
      setHoverIndex(idx);
    }
  };

  const dateStep = Math.max(1, Math.floor(series.length / 6));
  const xAxisTicks = series.filter((_, idx) => idx % dateStep === 0 || idx === series.length - 1);

  return (
    <div className="ins-card" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div className="ins-card-header">
        <div>
          <h3 className="ins-card-title">Orders per day &amp; Revenue Trend</h3>
          <p className="ins-card-subtitle">{subtitle}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", color: "#475569", fontWeight: 500 }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "9999px", backgroundColor: "#fb310d" }} /> {amountName} (₹, left scale)
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", color: "#475569", fontWeight: 500 }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "9999px", backgroundColor: "#4648d4" }} /> Completed orders (right scale)
          </span>
          <span
            style={{
              padding: "0.25rem 0.6rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              backgroundColor: "#f1f5f9",
              color: "#334155",
              borderRadius: "0.375rem",
            }}
          >
            Daily
          </span>
        </div>
      </div>

      <div className="ins-card-body" style={{ position: "relative" }}>
        <div
          className="ins-chart-container"
          role="img"
          aria-label={`${amountName} and completed orders per day, ${rangeLabel(range)}`}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
          style={{ cursor: "crosshair" }}
        >
          <svg
            className="w-full h-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox={`0 0 ${W} ${H}`}
            style={{ width: "100%", height: "100%", overflow: "visible" }}
          >
            <defs>
              <linearGradient id="revenueGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#fb310d" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#fb310d" stopOpacity="0.0" />
              </linearGradient>
              <linearGradient id="ordersGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#4648d4" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#4648d4" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {gridSteps.map((step) => {
              const y = padTop + chartH * (1 - step);
              const revVal = (maxRev * step) / 1000;
              return (
                <g key={step}>
                  <line stroke="#f1f5f9" strokeDasharray="4" strokeWidth="1" x1={padLeft} x2={W - padRight} y1={y} y2={y} />
                  <text fill="#94a3b8" fontFamily="Inter, sans-serif" fontSize="12" fontWeight="500" x="6" y={y + 4}>
                    ₹{revVal >= 1 ? `${Math.round(revVal)}k` : Math.round(revVal * 1000)}
                  </text>
                  <text fill="#4648d4" fontFamily="Inter, sans-serif" fontSize="12" fontWeight="600" x={W - padRight + 8} y={y + 4}>
                    {Math.round(maxDone * step)}
                  </text>
                </g>
              );
            })}
            <line stroke="#e2e8f0" strokeWidth="1" x1={padLeft} x2={W - padRight} y1={bottomY} y2={bottomY} />

            <path d={revArea} fill="url(#revenueGrad)" />
            <path d={revLine} stroke="#fb310d" strokeLinecap="round" strokeWidth="2.5" />

            <path d={doneArea} fill="url(#ordersGrad)" />
            <path d={doneLine} stroke="#4648d4" strokeDasharray="3 3" strokeWidth="2" />

            {activePt && (
              <g>
                <line
                  stroke="#fb310d"
                  strokeDasharray="2 2"
                  strokeWidth="1"
                  x1={activePt.x}
                  x2={activePt.x}
                  y1={padTop}
                  y2={bottomY}
                />
                <circle cx={activePt.x} cy={activePt.revY} fill="#fb310d" r="5" stroke="#ffffff" strokeWidth="2.5" />
                <circle cx={activePt.x} cy={activePt.doneY} fill="#4648d4" r="4" stroke="#ffffff" strokeWidth="2" />
              </g>
            )}
          </svg>

          {activePt && (
            <div
              className="ins-chart-tooltip"
              style={{
                left: `${(activePt.x / W) * 100}%`,
                top: `${(Math.min(activePt.revY, activePt.doneY) / H) * 100}%`,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: "4px", color: "#f8fafc", fontSize: "0.875rem" }}>
                {fmtDateWithWeekday(activePt.date)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8125rem" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "9999px", backgroundColor: "#fb310d" }} />
                <span>{amountName}: <strong>{rupeesWhole(activePt.revenue)}</strong></span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8125rem" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "9999px", backgroundColor: "#818cf8" }} />
                <span>Completed: <strong>{activePt.delivered}</strong></span>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", fontWeight: 500, color: "#64748b", padding: "0 1rem 0 2.5rem", marginTop: "0.5rem" }}>
          {xAxisTicks.map((d) => (
            <span key={d.date}>{dayLabel(d.date)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
